import { useRef, type ReactNode } from 'react';
import { useApPad } from '../../ui/use-ap-pad';
import { uiText } from '../../i18n';
import { platform } from '../../platform';
import type { CircleFormState } from './circle/CircleFieldInput';
import { record, text } from './studio-content';
import { studioDeliveryOffText, studioDeliveryView, studioOrderReceipt, studioOrderSteps } from './studio-order-tail-model';
import { StudioIcon } from './StudioIcon';

/** Хвост заказа «Фото» и «Видео» (APP-SPEC-5 §1.4, §2.5, §3, §16; BotRun): статус, этапы, результат, вторые действия,
    «Что дальше», главное «Открыть в чате», после сбоя — «Попробовать ещё раз» и «К настройкам», чек «Списано N C · остаток M C».
    Всё, что показывается, берётся из заказа сервера (`steps`, `display`, `charge`, `balance`, `error`); номера заказа на итоге нет. */
export interface StudioTailAction {
  label: string;
  onClick?(): void;
  href?: string;
  download?: string;
  disabled?: boolean;
  icon?: string;
}
export interface StudioTailTexts {
  run: string;
  runSub: string;
  done: string;
  doneSub: string;
  /** Подпись, когда доставка в чат ещё не подтверждена */
  donePending?: string;
  failed: string;
  /** Отказ проверки: что поменять («Заменить фото», «Изменить описание») */
  refusal: string;
  /** Место водяного знака в чеке со скидкой: «на фото», «на видео» */
  watermark: string;
}
const RUNNING = ['queued', 'running'];
export function StudioOrderTail({
  state,
  texts,
  hero,
  card,
  actions = [],
  next = [],
  delivered,
  onRefusal,
  children,
}: {
  state: CircleFormState;
  texts: StudioTailTexts;
  /** Герой: рамка по пропорциям заказа с картинкой (снимок, пример стиля) — пока идёт заказ и при сбое */
  hero?: ReactNode;
  /** Карточка «Стиль «Ретро-кадр» · Как у фото · 4 фото» */
  card?: string;
  actions?: StudioTailAction[];
  next?: StudioTailAction[];
  /** Доставка в чат подтверждена */
  delivered?: boolean;
  onRefusal?(): void;
  /** Результат: сетка вариантов, ролик */
  children?: ReactNode;
}) {
  const { order, busy, submissionBlocked } = state;
  const footRef = useRef<HTMLDivElement>(null);
  useApPad(footRef);
  const status = order?.status ?? 'queued';
  const error = record(record(order).error);
  const moderation = status === 'failed' && error.kind === 'moderation';
  const steps = studioOrderSteps(order);
  // Инструмент без пайплайна (docs/59 §2): заказ принят и ждёт подключения — это не сбой и не ход работы.
  const waiting = RUNNING.includes(status) && record(order).waiting_reason === 'pipeline_pending';
  const receipt = studioOrderReceipt(order, texts.watermark);
  // Бот сам не присылает (настройки, Q-221) — подпись не обещает чат, рядом ссылка «Настройки»
  const deliveryView = status === 'done' ? studioDeliveryView(order) : 'pending';
  const deliveryOff = studioDeliveryOffText(deliveryView);
  const box = RUNNING.includes(status) ? 'run' : status === 'done' ? 'done' : status === 'failed' ? 'fail' : 'run';
  const back = async () => {
    if (await state.returnToForm()) document.dispatchEvent(new CustomEvent('steos:studio:order-clear', { detail: { id: state.orderId } }));
  };
  const button = (action: StudioTailAction, className = 'btn') =>
    action.href ? (
      <a key={action.label} className={className} href={action.href} download={action.download}>
        {action.icon && <StudioIcon kind={action.icon} />}
        {uiText(action.label)}
      </a>
    ) : (
      <button key={action.label} type="button" className={className} disabled={busy || submissionBlocked || action.disabled} onClick={action.onClick}>
        {action.icon && <StudioIcon kind={action.icon} />}
        {uiText(action.label)}
      </button>
    );
  return (
    <div className={`ar pr studio-order-tail ${box}${moderation ? ' mod' : status === 'failed' ? ' tech' : ''}`}>
      {hero && box !== 'done' && <div className="pr-hero">{hero}</div>}
      {/* На итоге результат встаёт на место героя, над заголовком (BotRun, кадр photo-done) */}
      {box === 'done' && children}
      <div className="ar-tx">
        <h2>
          {uiText(
            status === 'done'
              ? texts.done
              : moderation
                ? 'Не прошло проверку'
                : status === 'failed'
                  ? texts.failed
                  : status === 'uncertain'
                    ? 'Уточняем результат'
                    : waiting
                      ? 'Заказ принят'
                      : texts.run,
          )}
        </h2>
        <p>
          {uiText(
            status === 'done'
              ? deliveryOff
                ? deliveryOff
                : delivered === false
                ? (texts.donePending ?? 'Бот пришлёт результат в чат, в отдельную тему.')
                : texts.doneSub
              : moderation
                ? text(error.message) || 'Измените запрос и попробуйте снова. Circle за проверенный заказ не возвращаются.'
                : status === 'failed'
                  ? 'Сбой на нашей стороне. Circle вернулись на баланс.'
                  : status === 'uncertain'
                    ? 'Результат пока не подтверждён. Проверьте этот заказ, прежде чем создавать новый.'
                    : waiting
                      ? 'Инструмент ещё подключается: заказ стоит в очереди и начнётся сам, бот пришлёт результат в чат.'
                      : texts.runSub,
          )}
        </p>
        {deliveryView === 'settings' && (
          <button type="button" className="ar-lnk" onClick={() => (window.location.hash = '#settings')}>
            {uiText('Настройки')}
          </button>
        )}
      </div>
      {!!steps.length && box !== 'done' && (
        <div className="tl studio-order-steps">
          {steps.map((step, index) => (
            <div
              key={step.key}
              className={`tli${step.status === 'done' || step.status === 'skipped' ? ' done' : step.status === 'running' ? ' now' : ''}${step.status === 'failed' ? ' bad' : ''}`}
            >
              <span className="ic">
                <StudioIcon kind={step.status === 'done' ? 'check' : 'sparkle'} />
                <span className="spn" />
              </span>
              <div className="t">
                <span>{step.title}</span>
                <small className="sr-only">{`${index + 1} / ${steps.length}`}</small>
              </div>
            </div>
          ))}
        </div>
      )}
      {box !== 'done' && children}
      <div className="ar-more">
        {card && status === 'done' && (
          <div className="ar-q">
            <p>{card}</p>
          </div>
        )}
        {status === 'done' && !!actions.length && <div className="ar-acts">{actions.map(action => button(action))}</div>}
        {status === 'done' && !!next.length && (
          <div className="pr-next">
            <span className="lb">{uiText('Что дальше')}</span>
            <div className="r">{next.map(action => button(action, ''))}</div>
          </div>
        )}
        {receipt && <p className="ar-rc">{receipt}</p>}
      </div>
      <div ref={footRef} className="ap-foot studio-native-foot">
        {status === 'done' ? (
          <button
            type="button"
            className="btn"
            onClick={() => {
              if (platform.kind === 'telegram') platform.close();
              else window.location.hash = '#history';
            }}
          >
            {uiText('Открыть в чате')}
          </button>
        ) : moderation ? (
          <button type="button" className="btn" disabled={busy || submissionBlocked} onClick={() => (onRefusal ? onRefusal() : void back())}>
            {uiText(texts.refusal)}
          </button>
        ) : status === 'failed' ? (
          <>
            <button type="button" className="btn" disabled={busy || submissionBlocked} onClick={() => void state.retry?.()}>
              {uiText('Попробовать ещё раз')}
            </button>
            <button type="button" className="ar-lnk" disabled={busy || submissionBlocked} onClick={() => void back()}>
              {uiText('К настройкам')}
            </button>
          </>
        ) : (
          <button type="button" className="btn off" aria-disabled="true" disabled>
            {uiText('Работа ещё создаётся')}
          </button>
        )}
      </div>
    </div>
  );
}
